// Third person camera: CG_OffsetThirdPersonView (cg_view.c). The camera sits
// cg_thirdPersonRange units behind the eye (rotated by cg_thirdPersonAngle
// around the player), pulled in front of walls by a small box trace, and
// pitched to look at a point 400 units ahead of the eye.

import { angleVectors, type Vec3 } from '../shared/math';

const FOCUS_DISTANCE = 400;
const CAMERA_MINS: Vec3 = [-4, -4, -4];
const CAMERA_MAXS: Vec3 = [4, 4, 4];

/** A box trace through the world (MASK_SOLID): where the box stops and how far it got. */
export type CameraTrace = (start: Vec3, mins: Vec3, maxs: Vec3, end: Vec3) => { fraction: number; endpos: Vec3 };

/**
 * @param eye first person view origin (origin + viewheight)
 * @param angles first person view angles
 * @param range distance behind the player (cg_thirdPersonRange, ET default 80)
 * @param angle rotation around the player in degrees (cg_thirdPersonAngle, 0 = behind)
 */
export function thirdPersonView(eye: Vec3, angles: Vec3, range: number, angle: number, trace: CameraTrace): { origin: Vec3; angles: Vec3 } {
  const focusAngles: Vec3 = [Math.min(angles[0], 45), angles[1], angles[2]]; // don't go too far overhead
  const forward: Vec3 = [0, 0, 0];
  angleVectors(focusAngles, forward, null, null);
  const focusPoint: Vec3 = [eye[0] + forward[0] * FOCUS_DISTANCE, eye[1] + forward[1] * FOCUS_DISTANCE, eye[2] + forward[2] * FOCUS_DISTANCE];

  let view: Vec3 = [eye[0], eye[1], eye[2] + 8];
  const viewAngles: Vec3 = [angles[0] * 0.5, angles[1], angles[2]];
  const right: Vec3 = [0, 0, 0];
  angleVectors(viewAngles, forward, right, null);
  const forwardScale = Math.cos((angle / 180) * Math.PI);
  const sideScale = Math.sin((angle / 180) * Math.PI);
  for (let i = 0; i < 3; i++) view[i] += -range * forwardScale * forward[i] - range * sideScale * right[i];

  // keep the camera out of walls: an 8x8x8 box from the eye
  let tr = trace(eye, CAMERA_MINS, CAMERA_MAXS, view);
  if (tr.fraction !== 1) {
    view = [tr.endpos[0], tr.endpos[1], tr.endpos[2] + (1 - tr.fraction) * 32];
    // a second trace: a tunnel ceiling may be close enough that this pokes out
    tr = trace(eye, CAMERA_MINS, CAMERA_MAXS, view);
    view = [...tr.endpos];
  }

  // pitch to look at the focus point from the camera
  const d: Vec3 = [focusPoint[0] - view[0], focusPoint[1] - view[1], focusPoint[2] - view[2]];
  const focusDist = Math.max(1, Math.hypot(d[0], d[1]));
  return { origin: view, angles: [(-180 / Math.PI) * Math.atan2(d[2], focusDist), angles[1] - angle, angles[2]] };
}
